// csvCurrencyDetect.js — H1 · Statement Import Single Authority (currency hotfix)
//
// Small, deterministic, LLM-free currency detector for the generic CSV import
// path. Runs entirely client-side over a bounded sample of headers + row cells.
//
// Contract (locked with user in Phase 81.5.4 H1):
//   • Only strong, explicit signals count. If no reliable signal exists,
//     return null and let the caller fall back to the selected account
//     currency for rendering, WITHOUT showing a mismatch warning.
//   • If conflicting currency signals are present, return null (never guess).
//   • Supported initial set: USD, INR, EUR, GBP, AED, SGD.
//     ISO codes are matched as whole words (case-insensitive).
//     Symbols recognized:
//       ₹ → INR
//       € → EUR
//       £ → GBP
//       $ → USD  (documented limitation: CAD/AUD/HKD/SGD also use "$"; without
//                further disambiguation we treat "$" as USD for this initial
//                set. Users on those currencies can still commit if the
//                selected account currency matches — see caller logic.)
//   • This module never mutates its inputs, never calls the network, and
//     never reads the DOM.
//
// Public API:
//   detectCurrencyFromCsv({ headers, rows }) → 'USD' | 'INR' | 'EUR' | 'GBP'
//                                             | 'AED' | 'SGD' | null
//
//     headers : string[]  — column headers as they appear in the CSV.
//     rows    : Array<Record<string, string>> — parsed row objects
//                                                (PapaParse `data`).
//
//   The caller should invoke this AFTER PapaParse produces rows but BEFORE
//   currency symbols get stripped from amount cells during preview build.

const SUPPORTED = ['USD', 'INR', 'EUR', 'GBP', 'AED', 'SGD'];

// Whole-word ISO code matcher for a single currency in a piece of text.
// Case-insensitive. Anchored at word boundaries so "USD" is a hit but
// "US Dollars" is not (the code stands alone in headers we care about).
const isoRegex = (code) => new RegExp(`(^|[^A-Z0-9])${code}([^A-Z0-9]|$)`, 'i');

// Symbol → ISO mapping. Only unambiguous symbols first; "$" handled below
// as a lower-confidence USD signal.
const UNAMBIGUOUS_SYMBOLS = [
  { symbol: '\u20B9', code: 'INR' }, // ₹
  { symbol: '\u20AC', code: 'EUR' }, // €
  { symbol: '\u00A3', code: 'GBP' }, // £
];

// Bounded scan: don't waste cycles on huge files. First N rows is enough
// to find a currency signal if one exists in a well-formed statement.
const MAX_ROWS_SCANNED = 200;
const MAX_CELLS_PER_ROW = 60;
const MAX_CELL_CHARS = 120;

function bumpVote(votes, code) {
  if (!code) return;
  votes[code] = (votes[code] || 0) + 1;
}

// Look for ISO currency codes in a text sample. Records a vote per code
// found. Multiple different codes in one string → multiple votes.
function scanIsoCodes(text, votes) {
  if (!text) return;
  for (const code of SUPPORTED) {
    if (isoRegex(code).test(text)) bumpVote(votes, code);
  }
}

// Look for unambiguous currency symbols in a text sample.
function scanUnambiguousSymbols(text, votes) {
  if (!text) return;
  for (const { symbol, code } of UNAMBIGUOUS_SYMBOLS) {
    if (text.indexOf(symbol) !== -1) bumpVote(votes, code);
  }
}

// Look for "$" and treat it as a USD hint for this initial supported set.
// Documented limitation: CAD/AUD/HKD/SGD also use "$". If a $ is present
// AND an ISO code like CAD/AUD/SGD is also present, callers will see
// conflicting signals and we'll return null (safe default).
function scanDollarHint(text, votes) {
  if (!text) return;
  if (text.indexOf('$') !== -1) bumpVote(votes, 'USD');
}

/**
 * Detect the statement currency from a parsed CSV.
 *
 * @param {Object} args
 * @param {string[]} [args.headers] — CSV column headers.
 * @param {Array<Record<string, string>>} [args.rows] — parsed row objects.
 * @returns {'USD'|'INR'|'EUR'|'GBP'|'AED'|'SGD'|null}
 */
export function detectCurrencyFromCsv({ headers = [], rows = [] } = {}) {
  const votes = Object.create(null);

  // 1) Header pass — most reliable signal. E.g. "Amount (USD)", "Debit INR".
  //    Also picks up ISO codes appearing on their own as header names.
  if (Array.isArray(headers)) {
    for (const h of headers) {
      const s = h == null ? '' : String(h);
      if (!s) continue;
      const sample = s.slice(0, MAX_CELL_CHARS);
      scanIsoCodes(sample, votes);
      scanUnambiguousSymbols(sample, votes);
      scanDollarHint(sample, votes);
    }
  }

  // 2) Row pass — bounded. Look at raw string cells for symbols + ISO codes.
  //    We scan ALL cells (not just amount columns) because the currency
  //    might live in a separate column named "Currency" or in a footer/
  //    metadata row that PapaParse surfaced.
  if (Array.isArray(rows)) {
    const n = Math.min(rows.length, MAX_ROWS_SCANNED);
    for (let i = 0; i < n; i += 1) {
      const row = rows[i];
      if (!row || typeof row !== 'object') continue;
      let cellsSeen = 0;
      for (const key in row) {
        if (!Object.prototype.hasOwnProperty.call(row, key)) continue;
        if (cellsSeen >= MAX_CELLS_PER_ROW) break;
        cellsSeen += 1;
        const v = row[key];
        if (v == null) continue;
        const s = typeof v === 'string' ? v : String(v);
        if (!s) continue;
        const sample = s.slice(0, MAX_CELL_CHARS);
        scanIsoCodes(sample, votes);
        scanUnambiguousSymbols(sample, votes);
        scanDollarHint(sample, votes);
      }
    }
  }

  // Tally distinct currencies with any signal.
  const found = Object.keys(votes).filter((c) => votes[c] > 0);
  if (found.length === 0) return null;    // no reliable signal
  if (found.length > 1) return null;      // conflicting signals — never guess
  return found[0];
}

/**
 * Public helper: is this ISO code one we currently detect?
 * Exposed for tests and for defensive checks in the caller.
 */
export function isSupportedCurrency(code) {
  return typeof code === 'string' && SUPPORTED.includes(code.toUpperCase());
}

export const _INTERNAL = { SUPPORTED, UNAMBIGUOUS_SYMBOLS };
